import React, { createContext, useCallback, useContext } from 'react';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import {
	useApplicantPersonsDetailsByReza,
	useApplicantLoader,
	useContactDetailsByReza,
	useProcessControl,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	LocalizeNameSpaceTypes,
	useLocale,
} from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { is } from '@gov-nx/core/types';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	FormData,
	ServiceContextControls,
	ServiceContextTypes,
} from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
	isBusinessPersonAllowed?: boolean;
}

const AUTHORIZATION_SERVICE_NAME = 'PODANI_ZADOST_O_INFORMACE';

export function PodaniZadostiOInformaceContextProvider({
	children,
	code,
	isBusinessPersonAllowed = false,
}: ServiceContextProviderProps) {
	const { t } = useLocale(code);
	const { t: tf } = useLocale(LocalizeNameSpaceTypes.Form);
	const { toastMessageSuccess } = useMessageEvents();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { downloadBlob } = useDownloadEvents();
	const { controls, setControls } = useProcessControl<ServiceContextControls>({
		initialLoading: true,
		downloadLoading: false,
		downloadError: null,
	});
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	useApplicantLoader({
		onError: (initialError) => {
			setControls({ initialLoading: false, initialError });
		},
		onLoaded: () => {
			if (controls.initialLoading) {
				setControls({ initialLoading: false });
			}
		},
	});

	const applicantPersonsDetails = useApplicantPersonsDetailsByReza({
		isBusinessPersonAllowed,
	});
	const contactDetails = useContactDetailsByReza({
		isBusinessPersonAllowed,
	});

	const formDefinition = FormInstance({
		code,
		person: contactDetails,
		dataBoxTypes: requiredDataBoxes,
		isDisabled: controls.processLoading || controls.downloadLoading,
		isLowAuthorization,
	});

	const submitMutation = usePoMutation({
		mutationFn: async (data: FormData) => {
			const prepared = prepareSubmitData(data);
			return sendSubmissionQuery(axiosInstance)(prepared);
		},
		onError: (error) => {
			setControls({ processError: error, processLoading: false });
			formDefinition.formReset();
		},
		onSuccess: async (data) => {
			setControls({ processLoading: false });

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('formular.zprava.odeslano'));
				formDefinition.formReset();
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const handleSubmit = useCallback(async () => {
		const values = formDefinition.formMethods.getValues();
		if (!is(values.odesilatelDSId)) {
			formDefinition.formMethods.setError('odesilatelDSId', {
				type: 'custom',
				message: tf('data-box.validations.vyberte-datovou-schranku', {
					namespace: LocalizeNameSpaceTypes.Form,
				}),
			});
		} else {
			setControls({ processError: null, processLoading: true });
			submitMutation.mutate(formDefinition.formMethods.getValues());
		}
	}, [formDefinition.formMethods, tf, setControls, submitMutation]);

	const handleDownload = useCallback(async () => {
		setControls({ downloadError: null, downloadLoading: true });
		return downloadBlob({
			data: prepareSubmitData(formDefinition.formMethods.getValues()),
			onSuccess: () => {
				setControls({ downloadLoading: false });
				formDefinition.formReset();
			},
			onError: (error) => {
				setControls({ downloadError: error, downloadLoading: false });
				formDefinition.formReset();
			},
		});
	}, [downloadBlob, formDefinition, setControls]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);
	const onDownload = formDefinition.formMethods.handleSubmit(handleDownload);

	return (
		<ServiceContext.Provider
			value={{
				code,
				formDefinition,
				submitMutation,
				onSubmit,
				controls,
				setControls,
				onDownload,
				requiredDataBoxes,
				applicantPersonsDetails,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const usePodaniZadostiInformaceContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
